import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../api';

/**
 * Gate-staff/organizer scanner: scans attendee QR tickets and admits them.
 * Route: /events/:eventId/scan
 */
const SCANNER_ELEM_ID = 'checkin-qr-reader';

export default function CheckInScanPage() {
  const { eventId } = useParams();
  // The URL may carry a slug (pretty link) or a numeric id. The check-in API is
  // keyed by numeric id, so resolve a slug to its id before calling it.
  const [numericId, setNumericId] = useState(/^\d+$/.test(eventId || '') ? eventId : null);
  useEffect(() => {
    if (numericId) return;
    let active = true;
    api.get(`/events/${eventId}/`)
      .then((res) => { if (active) setNumericId(res.data?.id ?? null); })
      .catch(() => {});
    return () => { active = false; };
  }, [eventId, numericId]);

  const scannerRef = useRef(null);
  const pausedRef = useRef(false);
  const lastCodeRef = useRef({ code: null, at: 0 });

  const [result, setResult] = useState(null); // { result, message, ticket }
  const [stats, setStats] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [manualCode, setManualCode] = useState('');

  const refreshStats = useCallback(async () => {
    if (!numericId) return;
    try {
      const res = await api.get(`/checkin/events/${numericId}/stats/`);
      setStats(res.data);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
    }
  }, [numericId]);

  const submitCode = useCallback(async (code) => {
    const trimmed = (code || '').trim();
    if (!trimmed || pausedRef.current || !numericId) return;

    // De-dupe rapid repeat scans of the same code.
    const now = Date.now();
    if (lastCodeRef.current.code === trimmed && now - lastCodeRef.current.at < 2500) return;
    lastCodeRef.current = { code: trimmed, at: now };

    pausedRef.current = true;
    try {
      const res = await api.post(`/checkin/events/${numericId}/scan/`, { code: trimmed });
      setResult(res.data);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      setResult(e.response?.data || { result: 'error', message: 'Scan failed.' });
    } finally {
      refreshStats();
      setTimeout(() => { pausedRef.current = false; }, 1500);
    }
  }, [numericId, refreshStats]);

  // Start the camera scanner.
  useEffect(() => {
    let mounted = true;
    const html5 = new Html5Qrcode(SCANNER_ELEM_ID);
    scannerRef.current = html5;

    html5
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decoded) => submitCode(decoded),
        () => {} // per-frame decode failures are normal; ignore
      )
      .catch((err) => {
        if (mounted) setCameraError(err?.message || 'Could not start the camera. Use manual entry below.');
      });

    return () => {
      mounted = false;
      const s = scannerRef.current;
      if (s) {
        s.stop().then(() => s.clear()).catch(() => {});
      }
    };
  }, [submitCode]);

  useEffect(() => { refreshStats(); }, [refreshStats]);

  if (forbidden) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <p className="text-red-600 max-w-sm">
          You don’t have permission to scan check-ins for this event. Only the event organizer can.
        </p>
      </div>
    );
  }

  const banner = resultBanner(result);

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Stats header */}
      <div className="px-4 py-3 bg-gray-800 flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-400">Checking in</p>
          <p className="font-semibold truncate max-w-[60vw]">{stats?.event_title || 'Event'}</p>
        </div>
        {stats && (
          <div className="text-right">
            <p className="text-2xl font-bold leading-none">
              {stats.checked_in}<span className="text-gray-400 text-base">/{stats.total_tickets}</span>
            </p>
            <p className="text-xs text-gray-400">{stats.percent_in}% in</p>
          </div>
        )}
      </div>

      {/* Result banner */}
      {result && (
        <div className={`px-4 py-4 text-center ${banner.bg}`}>
          <p className="text-2xl font-bold">{banner.icon} {banner.title}</p>
          {result.ticket?.attendee_name && (
            <p className="text-lg mt-1">{result.ticket.attendee_name}</p>
          )}
          {result.ticket?.seat_number && <p className="text-sm">Seat {result.ticket.seat_number}</p>}
          <p className="text-sm opacity-90 mt-1">{result.message}</p>
        </div>
      )}

      {/* Camera viewport */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <div id={SCANNER_ELEM_ID} className="w-full max-w-sm rounded-xl overflow-hidden bg-black" />
        {cameraError && <p className="mt-3 text-yellow-400 text-sm text-center">{cameraError}</p>}
      </div>

      {/* Manual entry fallback */}
      <div className="p-4 bg-gray-800">
        <form
          onSubmit={(e) => { e.preventDefault(); submitCode(manualCode); setManualCode(''); }}
          className="flex gap-2 max-w-sm mx-auto"
        >
          <input
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="Enter ticket code manually"
            className="flex-1 px-3 py-2 rounded bg-gray-700 text-white placeholder-gray-400 text-sm"
          />
          <button type="submit" className="px-4 py-2 rounded bg-indigo-600 font-medium">Check in</button>
        </form>
      </div>
    </div>
  );
}

function resultBanner(result) {
  if (!result) return {};
  switch (result.result) {
    case 'admitted':
      return { bg: 'bg-green-600', icon: '✅', title: 'Admitted' };
    case 'already_checked_in':
      return { bg: 'bg-yellow-600', icon: '⚠️', title: 'Already checked in' };
    case 'wrong_event':
      return { bg: 'bg-orange-600', icon: '🚫', title: 'Wrong event' };
    case 'not_found':
      return { bg: 'bg-red-600', icon: '❌', title: 'Ticket not recognised' };
    case 'void':
      return { bg: 'bg-red-600', icon: '❌', title: 'Ticket voided' };
    default:
      return { bg: 'bg-red-600', icon: '❌', title: 'Error' };
  }
}
